
import React from "react";
import { format } from "date-fns";
import { FILE_BASE_URL } from "../../../app/api";
import { 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  XCircle, 
  RotateCcw,
  User,
  MessageSquare,
  Share2
} from "lucide-react";
import { useState } from "react";

const statusConfig = {
  pending: { color: "text-amber-500", bg: "bg-amber-500/10", icon: Clock, label: "Pending" },
  in_progress: { color: "text-blue-500", bg: "bg-blue-500/10", icon: RotateCcw, label: "In Progress" },
  resolved: { color: "text-emerald-500", bg: "bg-emerald-500/10", icon: CheckCircle, label: "Resolved" },
  reopened: { color: "text-purple-500", bg: "bg-purple-500/10", icon: AlertCircle, label: "Reopened" },
  rejected: { color: "text-rose-500", bg: "bg-rose-500/10", icon: XCircle, label: "Rejected" },
};

const GrievanceCard = ({ grievance, onAction, isAdmin }) => {
  const { 
    id, title, description, attachment, status, 
    created_at, first_name, last_name, profile_pic,
    admin_first_name, admin_last_name, admin_note
  } = grievance;

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewOpen2, setPreviewOpen2] = useState(false);
  const [localStatus, setLocalStatus] = useState(status);

  const handleShareWhatsApp = (e) => {
    e.stopPropagation();
    const text = encodeURIComponent(
      `*Grievance Details*\n\n` +
      `*ID:* ${id}\n` +
      `*Title:* ${title}\n` +
      `*Description:* ${description.substring(0, 500)}${description.length > 500 ? '...' : ''}\n` +
      `*Status:* ${status.toUpperCase()}\n` +
      `*Reporter:* ${first_name} ${last_name}\n` +
      `*Date:* ${format(new Date(created_at), "MMM dd, yyyy")}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div
      className="
        relative
        overflow-hidden
        rounded-[22px]
        p-[1px]
        bg-gradient-to-br from-white/10 to-transparent
        group
      "
    >
      <div className="
        w-full
        flex flex-col
        h-[360px]
        rounded-[20px]
        backdrop-blur-xl
        bg-white/5
        border border-white/10
        shadow-[0_8px_30px_rgba(0,0,0,0.25)]
        hover:shadow-[0_12px_40px_rgba(0,0,0,0.35)]
        transition-all duration-300
      ">
        {/* Glow Effects */}
        <div className="absolute -top-16 -right-16 w-40 h-40 bg-indigo-500/20 rounded-full blur-3xl opacity-40 pointer-events-none"></div>
        <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-cyan-400/20 rounded-full blur-3xl opacity-40 pointer-events-none"></div>
        
        <div className="p-6 flex flex-col h-full">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl ${config.bg} ${config.color}`}>
                <Icon size={30} />
              </div>
              <div>
                <span className={`text-md font-semibold  tracking-wider text-white ${config.color}`}>
                  {config.label}
                </span>
                <p className="text-white-200 text-xs">
                  {format(new Date(created_at), "MMM dd, yyyy")}
                </p>
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2 text-slate-400">
                <div className="w-7 h-7 md:w-8 md:h-8 rounded-full overflow-hidden border border-slate-700 bg-slate-800">
                  {profile_pic ? (
                    <img 
                      src={`${FILE_BASE_URL}${profile_pic}`}
                      alt="" className="w-full h-full object-cover" 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-indigo-500/20 text-indigo-400">
                      <User size={14} />
                    </div>
                  )}
                </div>
                <span className="text-xs font-medium">{first_name} {last_name}</span>
                <button 
                  onClick={handleShareWhatsApp}
                  className="p-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 transition-all border border-emerald-500/20 ml-1"
                  title="Share on WhatsApp"
                >
                  <Share2 size={16} />
                </button>
              </div>
            )}
          </div>

          <h3 className="text-sm font-semibold text-white mb-1 line-clamp-1">
            {title}
          </h3>
          
          <p
            onClick={() => setPreviewOpen2(true)}
            className="text-white/80 text-xs mb-2 leading-relaxed line-clamp-2 cursor-pointer hover:text-white"
          >
            {description}
          </p>

          {attachment && (
            <button
              onClick={() => setPreviewOpen(true)}
              className="
                mb-4
                w-full
                py-2
                rounded-xl
                text-xs font-semibold
                text-white
                bg-white/10
                border border-white/20
                backdrop-blur-lg
                hover:bg-white/20
                transition-all
              "
            >
              👁 View Attachment
            </button>
          )}

          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare size={14} className="text-indigo-400" />
              <span className="text-xs font-semibold text-slate-300">
                Admin Note
              </span>
            </div>

            <p className="text-slate-400 text-xs italic line-clamp-2">
              {admin_note ? admin_note.replace(" [RESOLVED_ACCEPTED]", "") : "No note provided"}
            </p>

            {admin_first_name && admin_note && (
              <p className="text-right text-[10px] text-slate-500 mt-1">
                — {admin_first_name} {admin_last_name}
              </p>
            )}
          </div>

          <div className="pt-5 border-t border-white/10 mt-auto flex gap-2">
            {isAdmin ? (
              <>
                {status === 'pending' && (
                  <button 
                    onClick={() => onAction(id, 'in_progress')}
                    className="flex-1 py-2 rounded-xl text-xs shadow-lg shadow-indigo-500/20 font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 flex items-center justify-center gap-2"
                  >
                    Start Review
                  </button>
                )}
                {(status === 'pending' || status === 'in_progress' || status === 'reopened') && (
                  <>
                    <button 
                      onClick={() => onAction(id, 'resolved')}
                      className="flex-1 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-emerald-500 to-green-400 hover:from-emerald-400 hover:to-green-300 shadow-lg shadow-emerald-500/20 transition-all"
                    >
                      Resolve
                    </button>
                    <button 
                      onClick={() => onAction(id, 'rejected')}
                      className="flex-1 py-2 rounded-xl text-xs font-semibold text-white/80 bg-white/10 border border-white/20 backdrop-blur-lg hover:bg-rose-500 hover:text-white transition-all"
                    >
                      Reject
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                {status === 'resolved' && !admin_note?.includes("[RESOLVED_ACCEPTED]") ? (
                  <div className="flex gap-2 w-full pt-4">
                    <button 
                      onClick={() => {
                        onAction(id, 'resolved_accepted');
                        setLocalStatus('closed');
                      }}
                      className="flex-1 py-2 px-3 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 transition-colors text-white whitespace-nowrap"
                    >
                      Satisfied
                    </button>
                    <button 
                      onClick={() => {
                        onAction(id, 'reopened');
                        setLocalStatus('reopened');
                      }}
                      className="flex-1 py-2 rounded-xl text-xs font-semibold bg-rose-600 hover:bg-rose-500 transition-colors text-white flex items-center justify-center"
                    >
                      Not Satisfied
                    </button>
                  </div>
                ) : (status === 'resolved' && admin_note?.includes("[RESOLVED_ACCEPTED]")) || status === 'closed' || localStatus === 'closed' ? (
                  <div className="text-center text-xs text-emerald-400 font-medium w-full">
                    ✅ You marked this as satisfied
                  </div>
                ) : status === 'reopened' ? (
                  <div className="text-center text-xs text-yellow-400 font-medium w-full">
                    🔄 Reopened. Admin will review again
                  </div>
                ) : (
                  <div className="text-center text-xs text-white/40 w-full">
                    No actions available
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      {previewOpen && (
        <div
          onClick={() => setPreviewOpen(false)}
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center"
        >
          <img
            src={`${FILE_BASE_URL}/uploads/grievance/${attachment}`}
            className="max-h-[80vh] max-w-[90vw] rounded-xl shadow-2xl"
            alt=""
          />
        </div>
      )}

      {previewOpen2 && (
        <div
          onClick={() => setPreviewOpen2(false)}
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1e1b4b] p-5 rounded-xl w-[400px] max-h-[80vh] overflow-y-auto"
          >
            <h2 className="text-white font-semibold mb-2">{title}</h2>
            <p className="text-sm text-gray-300 mb-4">
              {description}
            </p>
            <button
              onClick={() => setPreviewOpen2(false)}
              className="mt-4 w-full py-2 bg-indigo-600 rounded-lg text-sm"
            >
              Close
            </button>
          </div>
        </div>
      )}  
    </div>
  );
};

export default GrievanceCard;
